/**
 * Ready-made rule packs and starting points for a spam policy, the platform's
 * or a tenant's. A pack's rules carry its id (`pack`), so the pack is added
 * and removed as a unit; applying anything only changes the draft, which the
 * admin saves as a new policy version like any other edit (and can roll back).
 *
 * Every rule uses only what the filter matches (zitimail_policy.lua): subject,
 * body, a header, link domains, attachment extensions; patterns are checked
 * by the API before they are saved.
 */

import type { SpamPolicy, SpamRule } from 'zitimail-shared-types';

export interface RulePack {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly rules: readonly SpamRule[];
}

const rule = (pack: string, r: Omit<SpamRule, 'pack' | 'mode'>): SpamRule => ({
  ...r,
  mode: 'enforce',
  pack,
});

export const RULE_PACKS: readonly RulePack[] = [
  {
    id: 'executables',
    name: 'Executable attachments',
    description:
      'Rejects programs and scripts attached directly (.exe, .js, .vbs, .lnk…). Real business mail almost never carries them.',
    rules: [
      rule('executables', {
        name: 'Executable attachment',
        field: 'attachment_ext',
        match: 'regex',
        value: '^(exe|scr|com|pif|cpl|msi|msp|bat|cmd|ps1|vbs|vbe|js|jse|wsf|wsh|hta|jar|lnk|reg)$',
        action: 'reject',
      }),
    ],
  },
  {
    id: 'risky-documents',
    name: 'Macro documents, disk images and HTML attachments',
    description:
      'Sends Office files with macros and disk images (.iso, .img) to Junk, and adds +4 for attached web pages, a common way to deliver fake sign-in pages.',
    rules: [
      rule('risky-documents', {
        name: 'Office file with macros',
        field: 'attachment_ext',
        match: 'regex',
        value: '^(docm|dotm|xlsm|xltm|xlam|pptm|potm|ppam|sldm)$',
        action: 'junk',
      }),
      rule('risky-documents', {
        name: 'Disk image attachment',
        field: 'attachment_ext',
        match: 'regex',
        value: '^(iso|img|vhd|vhdx)$',
        action: 'junk',
      }),
      rule('risky-documents', {
        name: 'Web page attachment',
        field: 'attachment_ext',
        match: 'regex',
        value: '^(html?|shtml|xhtml|svg)$',
        action: 'score',
        score: 4,
      }),
    ],
  },
  {
    id: 'credential-lures',
    name: 'Sign-in and mailbox lures',
    description:
      'Adds +3 for subjects like "verify your account", "password expires" or "mailbox full", the usual openings of credential phishing.',
    rules: [
      rule('credential-lures', {
        name: 'Account or mailbox lure in the subject',
        field: 'subject',
        match: 'regex',
        value:
          '(verify|confirm|validate|update|reactivate)\\s+(your\\s+)?(account|password|mailbox|email)|password\\s+(will\\s+)?expir|mailbox\\s+(is\\s+)?(full|over\\s+quota|storage)|unusual\\s+sign.?in|(account|mailbox)\\s+(is\\s+)?(suspended|disabled|locked)',
        action: 'score',
        score: 3,
      }),
    ],
  },
  {
    id: 'payment-lures',
    name: 'Payment and bank-detail lures',
    description:
      'Adds +3 for urgent payment requests, changed bank details and gift-card requests: the shape of business email compromise.',
    rules: [
      rule('payment-lures', {
        name: 'Urgent payment or bank change in the subject',
        field: 'subject',
        match: 'regex',
        value:
          '(urgent|immediate)\\s+(payment|wire|transfer)|(change|update)\\s+(of\\s+)?(bank|banking|account)\\s+details|gift\\s*cards?',
        action: 'score',
        score: 3,
      }),
      rule('payment-lures', {
        name: 'New bank details or gift cards in the body',
        field: 'body',
        match: 'regex',
        value:
          '(new|updated|changed)\\s+(bank|banking|account)\\s+(details|information)|(buy|purchase|get)\\s+(some\\s+)?(google\\s+play|apple|itunes|amazon|steam)\\s+gift\\s*cards?',
        action: 'score',
        score: 3,
      }),
    ],
  },
  {
    id: 'newsletters',
    name: 'Newsletter tolerance',
    description:
      'Takes 1.5 off mail that carries an unsubscribe header, so newsletters people signed up for land in Junk less often.',
    rules: [
      rule('newsletters', {
        name: 'Has an unsubscribe header',
        field: 'header:List-Unsubscribe',
        match: 'regex',
        value: '.',
        action: 'score',
        score: -1.5,
      }),
    ],
  },
];

export interface Preset {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** null: the level above's (a tenant's falls back to the platform's). */
  readonly add_header_score: number | null;
  readonly reject_score: number | null;
  readonly packs: readonly string[];
}

export const PRESETS: readonly Preset[] = [
  {
    id: 'relaxed',
    name: 'Relaxed',
    description: 'Junk at 8, reject at 15. Blocks executables; tolerant of newsletters.',
    add_header_score: 8,
    reject_score: 15,
    packs: ['executables', 'newsletters'],
  },
  {
    id: 'standard',
    name: 'Standard',
    description:
      'The usual scores (Junk at 6, reject at 12, or the platform’s). Blocks executables and risky documents; scores sign-in and payment lures.',
    add_header_score: null,
    reject_score: null,
    packs: ['executables', 'risky-documents', 'credential-lures', 'payment-lures'],
  },
  {
    id: 'strict',
    name: 'Strict',
    description: 'Junk at 4, reject at 10, with every pack except newsletter tolerance.',
    add_header_score: 4,
    reject_score: 10,
    packs: ['executables', 'risky-documents', 'credential-lures', 'payment-lures'],
  },
];

/** The ids of the packs whose rules are all in the policy. */
export function packsIn(policy: SpamPolicy): string[] {
  return RULE_PACKS.filter((p) => policy.rules.some((r) => r.pack === p.id)).map((p) => p.id);
}

/** The policy with a pack's rules added (once) or removed. */
export function withPack(policy: SpamPolicy, id: string, on: boolean): SpamPolicy {
  const others = policy.rules.filter((r) => r.pack !== id);
  const pack = RULE_PACKS.find((p) => p.id === id);
  return { ...policy, rules: on && pack ? [...others, ...pack.rules] : others };
}

/**
 * The policy as a preset leaves it: its scores and exactly its packs. The
 * sender and IP lists and hand-written rules stay as they are.
 */
export function withPreset(policy: SpamPolicy, preset: Preset): SpamPolicy {
  const own = policy.rules.filter((r) => !r.pack);
  const packs = RULE_PACKS.filter((p) => preset.packs.includes(p.id)).flatMap((p) => p.rules);
  return {
    ...policy,
    add_header_score: preset.add_header_score,
    reject_score: preset.reject_score,
    rules: [...own, ...packs],
  };
}
